import React from "react";
import {
    Box,
    Container,
    Flex,
    Heading,
    Text,
    Stack,
    List,
    chakra, VStack, AspectRatio,
} from "@chakra-ui/react";
import SectionCard from "../components/SectionCard";
import Highlight from "../components/Highlight";
import CTA from "../components/CTA";

const ConceptPage = () => {
    return (
        <Box>
            <SectionCard flexDirection="row" delay={0}>
                <Box flex="2" textAlign={{base: "center", md: "left"}}>
                    <chakra.img
                        src="./CAAT_Logo_large.png"
                        alt="Computer Assisted Animal Training Logo"
                        maxW="350px"
                        mx="auto"
                    />
                </Box>
                <Box flex="3">
                    <Heading as="h2" size="xl" mb={4}>
                        A Vision for Smarter Training — Together
                    </Heading>
                    <Stack spacing={4}>
                        <Text fontSize="md">
                            Imagine this: you arrive at work and decide which dogs you want to train today. With just a
                            few clicks, the system pulls up past records, analyzes recent progress, and drafts
                            a <Highlight>personalized training plan</Highlight> for each dog — ready for your review.
                        </Text>

                        <Text fontSize="md">
                            You and the assistant go over the plan together. You make adjustments, add your own ideas,
                            and refine the flow. Once it feels right, everything is <Highlight>synced to your mobile
                            device</Highlight>.
                        </Text>

                        <Text fontSize="md">
                            During training, the system supports you in the background —
                            offering <Highlight>reminders</Highlight>, <Highlight>tracking outcomes</Highlight>,
                            and <Highlight>logging events</Highlight> as they happen. You stay focused on the dog, while
                            the assistant takes care of documentation and structure. If something unexpected happens,
                            you can <Highlight>adapt the plan on the spot</Highlight> — and the system keeps up.
                        </Text>

                        <Text fontSize="md">
                            After the session, your records are already complete. You can <Highlight>review
                            progress</Highlight>, <Highlight>reflect on results</Highlight>, and — together with your
                            assistant — <Highlight>plan the next steps</Highlight>.
                        </Text>

                        <Text fontSize="md">
                            No juggling notebooks. No forgotten details. Just a <Highlight>smooth, intelligent
                            workflow</Highlight> that lets you focus on what you do best: working with dogs.
                        </Text>

                    </Stack>
                </Box>
            </SectionCard>
            <SectionCard flexDirection="row-reverse" delay={0.2}>
                <Box flex="1" textAlign="center">
                    <chakra.img
                        src="./training_video.png"
                        alt="Trainer supported by CAAT system"
                        maxW="400px"
                        mx="auto"
                    />
                </Box>

                <VStack flex="2" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Less Admin. More Clarity. Better Results.
                    </Heading>
                    <Text fontSize="md">
                        Training takes time — not just for the sessions, but for planning, documenting, tracking
                        progress,
                        and keeping everyone updated.
                    </Text>

                    <Text fontSize="md">
                        Technology can help you by <Highlight>cutting the admin</Highlight> and <Highlight>sharpening
                        the workflow</Highlight>:
                    </Text>

                    <List.Root spacing="3" ps="4">
                        <List.Item>Smart drafts instead of writing plans from scratch</List.Item>
                        <List.Item>Automatic documentation while you train</List.Item>
                        <List.Item>Progress you can show — without extra work</List.Item>
                        <List.Item>Reusable plans for new dogs and team members</List.Item>
                        <List.Item>Every dog’s history stored and ready</List.Item>
                    </List.Root>

                    <Text fontSize="md">
                        CAAT streamlines both training and admin — so you can get <Highlight>better results in less
                        time</Highlight>. </Text>
                </VStack>
            </SectionCard>
            <SectionCard flexDirection="row" delay={0.4}>
                <Box flex="2" textAlign="center">
                    <chakra.img
                        src="./DogBenefits_2.png"
                        alt="Happy dog during training"
                        maxW="300px"
                        mx="auto"
                    />
                </Box>

                <VStack flex="3" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Supporting the Dog — for Better Learning and Wellbeing
                    </Heading>

                    <Text fontSize="md">
                        At the heart of any training system is the dog. CAAT isn't just about making work easier for
                        humans —
                        it's about <Highlight>creating the best possible conditions</Highlight> for the animals we
                        train.
                    </Text>

                    <Text fontSize="md">
                        A well-structured session helps the dog stay <Highlight>focused and engaged</Highlight>. Clear
                        plans reduce
                        confusion, and consistent routines build confidence.
                    </Text>

                    <Text fontSize="md">
                        Because CAAT can adapt plans on the fly and track outcomes in detail, it's easier to spot when
                        something
                        isn’t working — and adjust. That means less frustration for both trainer and dog, and
                        more <Highlight>positive learning experiences</Highlight>.
                    </Text>

                    <Text fontSize="md">
                        Over time, this leads to <Highlight>faster progress</Highlight>, fewer setbacks, and a training
                        environment where the dog knows what to expect — and enjoys the process.
                    </Text>
                </VStack>
            </SectionCard>

            <SectionCard flexDirection="row-reverse" delay={0.5}>
                <Box flex="2">
                    <AspectRatio ratio={16 / 9}>
                        <iframe
                            title="Wearable sniffing detection"
                            src="https://www.youtube.com/embed/eX4JXxVoVhE"
                            allowFullScreen
                        />
                    </AspectRatio>
                    <Text fontSize="sm" color="gray.600" textAlign="center" mt={2}>
                        Early prototype demonstrating CAAT principles in action
                    </Text>
                </Box>

                <VStack flex="3" align="start" spacing={4}>
                    <Heading as="h2" size="lg">
                        Testing the Concept — Early Integration of CAAT Components
                    </Heading>

                    <Text fontSize="md">
                        This short video shows a simple, early prototype — built several years ago to explore the core
                        ideas behind
                        Computer-Assisted Animal Training. The setup may be basic, but it already brings together the
                        key components
                        that CAAT is built on.
                    </Text>

                    <Text fontSize="md">
                        In this case study, the goal was to teach a dog to stay on a mat, even when the trainer was out
                        of sight.
                        The system:
                    </Text>

                    <List.Root spacing={2} ps={4}>
                        <List.Item>
                            Used a camera and basic computer vision to <Highlight>observe</Highlight> the dog’s behavior
                        </List.Item>
                        <List.Item>
                            Followed structured timing and logic to <Highlight>guide</Highlight> the session
                        </List.Item>
                        <List.Item>
                            Triggered a feeder to <Highlight>reinforce</Highlight> success automatically
                        </List.Item>
                        <List.Item>
                            Logged results in real time to <Highlight>document</Highlight> progress
                        </List.Item>
                    </List.Root>

                    <Text fontSize="md">
                        What’s important isn’t the technology itself — it’s how the <CTA to="/plantraindoc">App</CTA>,
                        the <CTA to="/sensors">Sensors</CTA>, and the <CTA to="/training_aids">Training Aids</CTA> all
                        worked together to support the training process. Even in this early form, the system could take
                        over routine tasks, provide structure, and let the trainer focus on the bigger picture.
                    </Text>

                    <Text fontSize="md">
                        It’s a simple example — but a powerful one. A first step toward making training more
                        independent, measurable, and consistent.
                    </Text>
                </VStack>
            </SectionCard>

        </Box>
    );
}

export default ConceptPage;